import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { apiClient } from "app/modules/api-client/apiClient";
import { getFlattenSelectedCategory } from "app/pages/industry-analysis/common/getSelectedCategory";

import type { GrowthOverviewGranularity, GrowthOverviewMetric } from "../const";
import {
    buildGrowthOverviewQueryParams,
    getGrowthOverviewSortBy,
    normalizeGrowthOverviewSearch,
} from "../utils/buildGrowthOverviewQueryParams";
import { buildGrowthOverviewRootQueryKey } from "../utils/buildGrowthOverviewRootQueryKey";
import { buildGrowthOverviewRootPaginationParams } from "../utils/resolveGrowthOverviewCategoryLevel";

export const useGrowthOverviewData = (
    granularity: GrowthOverviewGranularity,
    metric: GrowthOverviewMetric,
    debouncedSearchTerm: string,
) => {
    const { params } = useCurrentStateAndParams();
    const { category, country, duration, webSource, comparedDuration, includeSubdomains } = params;

    const search = normalizeGrowthOverviewSearch(debouncedSearchTerm);
    const sortBy = getGrowthOverviewSortBy(metric);
    const queryKey = useMemo(() => {
        const rootPaginationParams = buildGrowthOverviewRootPaginationParams(getFlattenSelectedCategory());

        return buildGrowthOverviewRootQueryKey({
            granularity,
            sortBy,
            search,
            category,
            country,
            duration,
            webSource,
            comparedDuration,
            includeSubdomains,
            rootPaginationParams,
        });
    }, [granularity, sortBy, search, category, country, duration, webSource, comparedDuration, includeSubdomains]);

    const query = useQuery({
        queryKey,
        queryFn: () =>
            apiClient.IndustryAnalysis.industriesBenchmarkGrowthOverviewList(
                buildGrowthOverviewQueryParams({
                    granularity,
                    sortBy,
                    search,
                    ...buildGrowthOverviewRootPaginationParams(getFlattenSelectedCategory()),
                }),
            ),
    });

    return { ...query, queryKey };
};
